<template>
  <div class="navigationPageButton mx-n3 px-3">
    <p v-if="selectedAmountText" class="mb-1 text-blue-500 text-md text-center text-md-right">
      {{ addValueText }}
      <span class="font-weight-bold">{{ selectedAmountText }} {{ $t('form.label.pln') }}</span>
    </p>
    <router-link :to="donorDataRoute" v-slot="{ href, navigate }" custom>
      <span class="font-responsive">
        <b-button
          :href="href"
          variant="salmon-dark"
          block
          class="text-base-mobile-lg font-weight-bold py-2 line-height-lg angle-right-icon-hover"
          :disabled="disabled"
          @click="navigate"
        >
          {{ $t('form.typeOfSupport.next') }}
          <i class="angle-right-icon ml-1 bg-white"></i>
        </b-button>
      </span>
    </router-link>
    <FormSteps class="d-md-none steps-mobile" />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'
import { storeToRefs } from 'pinia'
import { useIsFieldValid } from 'vee-validate'
import { useRoute, type RouteLocationNamedRaw } from 'vue-router'
import { useFieldValue } from 'vee-validate'

import FormSteps from '@/components/widgets/FormSteps.vue'
import { useSupportText } from '@/composables'
import { useButtonDelay } from '@/composables/useButtonDelay'
import useTransactionDataStore from '@/stores/transactionData'

import { PaymentType } from '@/types'
import { SOFT_PAYMENT_LIMIT } from '@/constants/payment'

const { addValueTextByType } = useSupportText()

const transactionDataStore = useTransactionDataStore()
const otherAmount = useFieldValue('otherAmount')
const { amount, paymentType } = storeToRefs(transactionDataStore)

const addValueText = computed<string>(() => {
  return addValueTextByType(paymentType.value)
})

const isValid = useIsFieldValid('otherAmount')
const { isTemporarilyDisabled } = useButtonDelay()
const disabled = computed<boolean>(() => {
  // if user select default amount but it's not valid
  if (amount.value === -1 && isValid.value !== true) return true
  // if user didn't select any amount
  if (amount.value === null) {
    return true
  }
  return isTemporarilyDisabled.value
})

const selectedAmountText = computed(() => {
  if (paymentType.value === PaymentType.instalments || paymentType.value === PaymentType.purchase) {
    if (disabled.value) return null
    return amount.value
  }
  if (amount.value === -1) {
    return otherAmount.value
  }
  return (amount.value || otherAmount.value) ?? SOFT_PAYMENT_LIMIT
})

const route = useRoute()
const donorDataRoute = reactive<RouteLocationNamedRaw>({
  name: 'FormOfDonorData',
  params: {
    childEditionId: route.params?.childEditionId
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/app';

.navigationPageButton {
  @include media-breakpoint-down(sm) {
    background-color: white;
    position: sticky;
    bottom: 0;
    margin: var(--spacer-2) calc(-1 * var(--spacer-3));
    padding: var(--spacer-2) 0;
    z-index: 4;
  }
}
</style>
